/** The Shopify code-discount type a coupon is minted as. "basic" is amount off
 *  products or order; an absent kind reads as basic. */
export type ShopifyDiscountKind = 'basic' | 'free_shipping' | 'bxgy';

/** Free shipping's own settings. No countries means every country; no
 *  max_shipping_price means no shipping rate is excluded. */
export interface ShopifyFreeShippingTerms {
  countries?: string[];
  max_shipping_price?: number;
}

export type ShopifyBxgyBuysType = 'quantity' | 'amount';

export interface ShopifyBxgyItemRef {
  id: string;
  title: string;
  handle?: string;
  image_url?: string;
}

/** Buy X Get Y's own settings. What the customer buys is the coupon's
 *  target_type/products/collections; what they get is here. gets_pct 100 is
 *  Shopify's "Free". */
export interface ShopifyBxgyTerms {
  buys_type: ShopifyBxgyBuysType;
  buys_quantity?: number;
  buys_amount?: number;
  gets_quantity: number;
  gets_target_type: 'product' | 'collection';
  gets_products?: ShopifyBxgyItemRef[];
  gets_collections?: ShopifyBxgyItemRef[];
  gets_pct: number;
  uses_per_order_limit?: number;
}

/** Carried by the campaign config, the coupon terms and every coupon form. */
export interface ShopifyDiscountKindTerms {
  discount_kind?: ShopifyDiscountKind;
  free_shipping?: ShopifyFreeShippingTerms;
  bxgy?: ShopifyBxgyTerms;
}

export const DEFAULT_BXGY: ShopifyBxgyTerms = {
  buys_type: 'quantity',
  buys_quantity: 1,
  gets_quantity: 1,
  gets_target_type: 'product',
  gets_products: [],
  gets_pct: 100,
};

export function discountKindOf(
  t: ShopifyDiscountKindTerms | null | undefined,
): ShopifyDiscountKind {
  return t?.discount_kind ?? 'basic';
}

/** The kind fields a form sends: only the active kind's settings. A basic
 *  discount sends none; the server reads an absent kind as basic. */
export function kindPayload(t: ShopifyDiscountKindTerms): ShopifyDiscountKindTerms {
  const kind = discountKindOf(t);
  if (kind === 'free_shipping')
    return { discount_kind: kind, free_shipping: t.free_shipping ?? {} };
  if (kind === 'bxgy') {
    const { buys_amount, buys_quantity, ...bxgy } = t.bxgy ?? DEFAULT_BXGY;
    return {
      discount_kind: kind,
      bxgy: bxgy.buys_type === 'amount' ? { ...bxgy, buys_amount } : { ...bxgy, buys_quantity },
    };
  }
  return {};
}

interface KindFormFields extends ShopifyDiscountKindTerms {
  include_pct_in_code?: boolean;
  minimum_requirement_type?: string;
  minimum_requirement_value?: number;
  target_type?: string;
  products?: unknown[];
  collections?: unknown[];
}

/** A coupon form body as the server accepts it: only the active kind's
 *  settings, no value in the code for a type without one, free shipping
 *  store-wide, and no minimum on Buy X Get Y. */
export function withKindPayload<T extends KindFormFields>(input: T): T {
  const kind = discountKindOf(input);
  const { discount_kind: _kind, free_shipping: _fs, bxgy: _bxgy, ...rest } = input;
  const out = { ...rest, ...kindPayload(input) } as T;
  if (kind === 'basic') return out;
  const noValue = 'include_pct_in_code' in out ? { include_pct_in_code: false } : {};
  const noEachItem = 'applies_on_each_item' in out ? { applies_on_each_item: false } : {};
  if (kind === 'free_shipping') {
    return {
      ...out,
      ...noValue,
      ...noEachItem,
      target_type: 'entire_store',
      products: [],
      collections: [],
      ...('variants' in out ? { variants: [] } : {}),
    };
  }
  const noMinimum =
    'minimum_requirement_type' in out
      ? { minimum_requirement_type: '', minimum_requirement_value: 0 }
      : {};
  const noCycleLimit = 'purchase_type' in out ? { recurring_cycle_limit: 0 } : {};
  return { ...out, ...noValue, ...noEachItem, ...noMinimum, ...noCycleLimit };
}
